import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ArrowRight, Camera, HeartHandshake } from 'lucide-react';
import Seo from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import { BUSINESS } from '@/seo/seoConfig';
import { CHURCH } from '@/api/client';

export default function Privacy() {
  return (
    <div>
      <Seo
        title="Privacy Notice | Ministry of Jesus, Dublin"
        titleExact
        description="How Ministry of Jesus (Registered Charity RCN 20207036) collects, uses and protects personal data of members, donors and website visitors under Irish GDPR."
        path="/privacy"
        keywords={['Ministry of Jesus privacy policy', 'church GDPR Ireland', 'RCN 20207036 data protection']}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Privacy Notice', path: '/privacy' }])]}
      />

      <section className="gradient-purple text-white py-10 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
            <Shield size={14}/> Data Protection
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-3">Privacy Notice</h1>
          <p className="text-white/85">Last updated: {new Date().toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-moj-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 prose-moj space-y-6">
          <p>
            Ministry of Jesus Ireland (“we”, “us”, “the Church”) is a Pentecostal church and Registered Charity (RCN <strong>20207036</strong>)
            based at {BUSINESS.address.streetAddress}, {BUSINESS.address.addressLocality}, {BUSINESS.address.postalCode}. We are committed to
            protecting the privacy and personal data of our members, donors, event attendees and website visitors in line with the
            Irish Data Protection Act 2018 and the General Data Protection Regulation (GDPR).
          </p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">1. Information we collect</h2>
          <p>We collect personal data only when necessary to fulfil our charitable and ministry work. This may include:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Contact details (name, email, phone number, address) when you register as a member, RSVP for an event, sign up for VBS, or submit a prayer request.</li>
            <li>Donation details (amount, category, payment status) when you give through our website. Card details are handled solely by Stripe and are never stored by us.</li>
            <li>Family information (children’s names, ages, medical notes) when a parent registers a child for Sunday School or VBS.</li>
            <li>Prayer notes and pastoral requests that you choose to share.</li>
            <li>Standard technical data (IP address, browser type, pages visited) via cookies and analytics.</li>
          </ul>

          <h2 className="font-serif text-2xl text-moj-purple-deep">2. How we use your information</h2>
          <p>Your personal data is used only for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Church administration — membership records, event coordination, safeguarding.</li>
            <li>Communication — sending confirmations, receipts, prayer responses and church newsletters (only where consent is given).</li>
            <li>Donation records — receipt issuance and charity reporting (including Charitable Donation Scheme where applicable).</li>
            <li>Pastoral care — following up on prayer requests, hospital visits and personal support.</li>
            <li>Legal compliance — meeting our obligations as an Irish registered charity.</li>
          </ul>

          <h2 className="font-serif text-2xl text-moj-purple-deep">3. Legal basis for processing</h2>
          <p>We rely on the following legal bases under GDPR Article 6: <em>consent</em> (which you can withdraw at any time), <em>contract</em> (e.g. donation records),
            <em> legitimate interests</em> (running the church and communicating with members) and <em>legal obligation</em> (charity reporting, safeguarding).</p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">4. Who we share your information with</h2>
          <p>We do not sell or trade your data. We share personal information only with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Stripe (payment processing for donations).</li>
            <li>Emergent (secure hosting infrastructure).</li>
            <li>Google (Sign-in with Google, if you choose to use it, and YouTube for public embeds).</li>
            <li>Irish authorities (Revenue, Charities Regulator, Túsla) where legally required.</li>
          </ul>

          <h2 className="font-serif text-2xl text-moj-purple-deep">5. How long we keep your data</h2>
          <p>We keep personal data only as long as necessary: donation records for 7 years (Revenue requirement), safeguarding records
            for 25 years (Túsla guidance), and general membership data until you request removal or membership ends.</p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">6. Your rights</h2>
          <p>You have the right to access, rectify, erase, restrict, or port your personal data, and to object to processing. To exercise any of these
            rights, contact us at <a className="text-moj-purple underline" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>. You may also lodge a complaint with the
            <a className="text-moj-purple underline ml-1" href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer">Data Protection Commission of Ireland</a>.</p>

          <h2 className="font-serif text-2xl text-moj-purple-deep">7. Cookies</h2>
          <p>Our website uses only essential cookies for session management and payment security. We do not use advertising or profiling cookies.</p>

          {/* -------- Photography, Video Recording & Live Streaming -------- */}
          <div id="photography" className="scroll-mt-24 pt-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl gradient-purple flex items-center justify-center flex-shrink-0">
                <Camera size={20} className="text-moj-gold" />
              </div>
              <h2 className="font-serif text-2xl text-moj-purple-deep m-0">
                8. Photography, Video Recording &amp; Live Streaming
              </h2>
            </div>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Ministry of Jesus may photograph, video record, or live-stream selected
                church services, worship gatherings, conferences, programmes, and special
                events.
              </li>
              <li>
                Live broadcasts and recorded content may appear on the Ministry of Jesus
                website, official YouTube channel, Facebook, Instagram, and other authorised
                church communication platforms.
              </li>
              <li>
                Clear notices will be displayed at appropriate entrances or areas whenever
                photography, video recording, or live streaming takes place.
              </li>
              <li>
                Church attendance does not automatically constitute consent for individually
                focused photographs or promotional recordings where specific consent is
                required.
              </li>
              <li>
                Any attendee wishing to avoid appearing prominently in photography or video
                may speak with a member of the Media Team before the service or event.
                Reasonable arrangements will be provided wherever practicable.
              </li>
            </ul>
          </div>

          {/* -------- Children & Young People -------- */}
          <div id="children" className="scroll-mt-24">
            <div className="rounded-2xl border-2 border-moj-gold/40 bg-white/70 p-5 sm:p-6 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-moj-gold/20 flex items-center justify-center flex-shrink-0">
                  <HeartHandshake size={20} className="text-moj-gold-deep" />
                </div>
                <h3 className="font-serif text-xl text-moj-purple-deep m-0">
                  Children &amp; Young People
                </h3>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-moj-charcoal">
                <li>
                  Ministry of Jesus recognises the importance of additional privacy and
                  safeguarding measures when photographs or videos involve children and
                  young people.
                </li>
                <li>
                  Identifiable photographs or video recordings of children intended for
                  publication on the church website, YouTube channel, social-media
                  platforms, printed publicity, or other public-facing church communications
                  will normally require written consent from a parent or legal guardian.
                </li>
                <li>
                  Parent or guardian consent will be obtained through an appropriate
                  media-consent process before identifiable photographs or recordings are
                  intentionally published.
                </li>
                <li>
                  Where media consent has not been provided, reasonable steps will be taken
                  to prevent a child from being individually photographed, prominently
                  recorded, or intentionally featured in published church media.
                </li>
                <li>
                  Special care must be taken not to publish unnecessary identifying
                  information concerning children, including full names, personal contact
                  information, addresses, school information, or other information capable
                  of identifying or locating a child.
                </li>
                <li>
                  Media consent may be withdrawn for future use by contacting the church
                  through the privacy contact details provided below.
                </li>
              </ul>
            </div>
          </div>

          <h2 className="font-serif text-2xl text-moj-purple-deep">9. Contact</h2>
          <p>
            Questions about this notice? Contact our Data Protection point of contact at{' '}
            <a className="text-moj-purple underline" href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>{' '}or call{' '}
            <a className="text-moj-purple underline" href={`tel:${BUSINESS.phone.replace(/\s/g,'')}`}>{BUSINESS.phone}</a>.
          </p>

          {/* -------- Privacy / Safeguarding contact block -------- */}
          <div className="card-moj p-5 sm:p-6 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={18} className="text-moj-gold-deep" />
              <h3 className="font-serif text-lg text-moj-purple-deep m-0">
                Privacy / Safeguarding Contact
              </h3>
            </div>
            <dl className="grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">Email</dt>
                <dd>
                  <a className="text-moj-purple underline break-all" href={`mailto:${CHURCH.emailInfo}`}>
                    {CHURCH.emailInfo}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">Phone</dt>
                <dd>
                  <a className="text-moj-purple underline" href={`tel:${CHURCH.phoneIntl.replace(/\s/g,'')}`}>
                    {CHURCH.phoneIntl}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">Website</dt>
                <dd>
                  <a className="text-moj-purple underline break-all" href={`https://${CHURCH.website}`} target="_blank" rel="noopener noreferrer">
                    {CHURCH.website}
                  </a>
                </dd>
              </div>
            </dl>
          </div>

          <div className="card-moj p-5 flex items-center gap-3 mt-8">
            <Shield size={22} className="text-moj-gold-deep"/>
            <div className="text-sm text-moj-charcoal">Registered Charity Number: <strong className="text-moj-purple-deep">RCN 20207036</strong></div>
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" className="btn-primary"><Mail size={16}/> Contact Us <ArrowRight size={16}/></Link>
            <Link to="/safeguarding" className="btn-outline">Safeguarding Policy</Link>
            <Link to="/faq" className="btn-outline">Photography &amp; Live Streaming FAQ</Link>
          </div>

          <p className="text-xs text-moj-charcoal/60 mt-6 text-right italic">
            Last updated: {new Date().toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>
    </div>
  );
}
